/**
 * ORO MVP Auth Context
 * 
 * Authentication state management using React Query for /me endpoint.
 * Provides backwards-compatible API while using TanStack Query internally.
 */

import { createContext, useContext, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import type { Me } from '@/lib/api/auth';
import { logout as globalLogout } from '@/lib/auth';
import { useMe } from '@/hooks/useMe';

export interface AuthContextValue {
  user: Me | null;
  me: Me | null; // Alias for compatibility
  isLoading: boolean;
  isAuthenticated: boolean;
  logout: () => Promise<void>;
  refetch: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export { AuthContext };

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const queryClient = useQueryClient();
  const location = useLocation();
  
  // Public paths that don't require authentication
  const isPublicPage = ['/login', '/signup', '/reset-password', '/forgot-password', '/faq', '/privacy', '/terms', '/about']
    .includes(location.pathname);
  
  // Only call useMe on non-public pages
  const { data: user, isLoading, error, refetch } = useMe({ enabled: !isPublicPage });

  /**
   * Logout user and clear all cached data
   * Uses centralized logout function shared with QueryClient 401 handler
   */
  const logout = async () => {
    await globalLogout(queryClient);
  };

  /**
   * Refetch current user data
   */
  const refetchUser = async () => {
    await refetch();
  };

  const value: AuthContextValue = {
    user: user || null,
    me: user || null, // Alias for compatibility
    isLoading,
    isAuthenticated: !!user && !error,
    logout,
    refetch: refetchUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook to use auth context
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}